import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Reveal from "../components/Reveal";
import { useScrollZoom } from "../hooks/useScrollZoom";
import { MAILTO, CONTACT_EMAIL } from "../lib/brand";

const HeroSection = () => {
    const balanceZoom = useScrollZoom({ min: 0.96, max: 1.05, peak: 0.4 });

    return (
    <section
        data-testid="hero-section"
        className="relative pt-28 md:pt-32 pb-16 md:pb-20 overflow-hidden"
    >
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Left: copy */}
            <div className="lg:col-span-7">
                <Reveal delay={120}>
                    <h1
                        data-testid="hero-headline"
                        className="headline-display mt-2 text-2xl sm:text-3xl lg:text-4xl font-semibold text-black"
                    >
                        Mobile rewards,
                        <br />
                        <span className="text-neutral-400">made simple.</span>
                    </h1>
                </Reveal>
                <Reveal delay={220}>
                    <p
                        data-testid="hero-subheadline"
                        className="mt-6 text-sm md:text-base text-neutral-600 max-w-lg leading-relaxed"
                    >
                        FreeByte helps brands reward users with mobile value
                        they can earn, save and redeem later.
                    </p>
                </Reveal>
                <Reveal delay={320}>
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <Link
                            to="/how-it-works"
                            data-testid="hero-how-it-works-btn"
                            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-black text-white text-[13px] font-medium tracking-wide hover:bg-neutral-800 transition-colors group"
                        >
                            How It Works
                            <ArrowRight
                                size={15}
                                className="transition-transform group-hover:translate-x-0.5"
                            />
                        </Link>
                        <a
                            href={MAILTO}
                            data-testid="hero-contact-btn"
                            className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-black/15 text-black text-[13px] font-medium tracking-wide hover:border-black transition-colors"
                        >
                            Contact Us
                            <ArrowUpRight size={15} />
                        </a>
                    </div>
                </Reveal>
                <Reveal delay={420}>
                    <p
                        data-testid="hero-conversion-note"
                        className="mt-6 text-[10px] uppercase tracking-[0.18em] text-neutral-400"
                    >
                        1,000 FreeByte ≈ 1 GB
                    </p>
                </Reveal>
            </div>

            {/* Right: balance card */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
                <Reveal delay={300} className="w-full max-w-[340px]">
                    <div
                        ref={balanceZoom.ref}
                        style={balanceZoom.style}
                        data-testid="hero-balance-card"
                        className="float-soft relative bg-white border border-black/10 rounded-2xl p-8 md:p-10 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.25)] min-h-[440px] flex flex-col"
                    >
                        <div className="flex items-center justify-between">
                            <p className="text-[10px] uppercase tracking-[0.24em] text-neutral-400">
                                FreeByte Balance
                            </p>
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-black" />
                        </div>
                        <div className="mt-12">
                            <p
                                data-testid="hero-balance-amount"
                                className="text-4xl md:text-5xl font-semibold tracking-tight text-black"
                            >
                                12,500{" "}
                                <span className="text-neutral-400 text-2xl md:text-3xl align-baseline font-medium">
                                    FB
                                </span>
                            </p>
                            <p className="mt-3 text-xs text-neutral-500">
                                ≈ 12.5 GB
                            </p>
                        </div>
                        <div className="mt-auto pt-6 border-t border-black/5 flex items-center justify-between">
                            <p className="text-[11px] text-neutral-400 tracking-wide">
                                Ready to redeem
                            </p>
                            <p className="text-[11px] text-black tracking-wide">
                                Data · Airtime
                            </p>
                        </div>
                    </div>
                </Reveal>
            </div>
        </div>
    </section>
    );
};

const HowItWorksSection = () => {
    const items = [
        {
            id: "brand",
            title: "Brand defines goal",
            body: "FreeByte builds a custom-made campaign designed to grow the brand's specific objective.",
        },
        {
            id: "earn",
            title: "Earn",
            body: "Users earn FreeByte by participating in the brand's campaign.",
        },
        {
            id: "save",
            title: "Save",
            body: "Users collect and keep their FreeByte balance over time.",
        },
        {
            id: "redeem",
            title: "Redeem",
            body: "Users redeem FreeByte for mobile data or airtime when needed.",
        },
        {
            id: "reporting",
            title: "Reporting & optimization",
            body: "FreeByte reports results back to the brand and continuously optimizes the campaign.",
        },
    ];

    return (
        <section
            id="how-it-works"
            data-testid="home-how-section"
            className="border-t border-black/5 py-20 md:py-24"
        >
            <div className="max-w-6xl mx-auto px-6 md:px-10">
                <div className="max-w-3xl">
                    <Reveal>
                        <p className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">
                            How it works
                        </p>
                    </Reveal>
                    <Reveal delay={100}>
                        <h2
                            data-testid="home-how-headline"
                            className="headline-display mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold text-black"
                        >
                            Earn. Save.{" "}
                            <span className="text-neutral-400">Redeem.</span>
                        </h2>
                    </Reveal>
                </div>

                <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-5 md:gap-6">
                    {items.map((item, idx) => (
                        <Reveal key={item.id} delay={120 * (idx + 1)}>
                            <div
                                data-testid={`how-card-${item.id}`}
                                className="group h-full min-h-[280px] flex flex-col rounded-2xl border border-black/10 bg-white p-7 md:p-8 transition-all duration-500 hover:border-black/40 hover:shadow-[0_20px_60px_-30px_rgba(0,0,0,0.2)]"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-[11px] tracking-[0.22em] uppercase text-neutral-400">
                                        Step 0{idx + 1}
                                    </span>
                                    <span className="w-6 h-px bg-black/10 group-hover:bg-black transition-colors duration-500" />
                                </div>
                                <h3 className="mt-12 text-xl md:text-2xl font-semibold text-black tracking-tight">
                                    {item.title}
                                </h3>
                                <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
                                    {item.body}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
};

const ContactSection = () => {
    const onSubmit = (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        const name = form.elements.namedItem("name")?.value || "";
        const email = form.elements.namedItem("email")?.value || "";
        const message = form.elements.namedItem("message")?.value || "";
        const subject = encodeURIComponent(
            `FreeByte Inquiry${name ? ` — ${name}` : ""}`
        );
        const body = encodeURIComponent(
            `Name: ${name}\nEmail: ${email}\n\n${message}`
        );
        window.location.href = `${MAILTO}?subject=${subject}&body=${body}`;
    };

    return (
        <section
            id="contact"
            data-testid="home-contact-section"
            className="border-t border-black/5 py-20 md:py-24 bg-neutral-50/60"
        >
            <div className="max-w-6xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-6">
                    <Reveal>
                        <p className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">
                            Get in touch
                        </p>
                    </Reveal>
                    <Reveal delay={100}>
                        <h2
                            data-testid="contact-headline"
                            className="headline-display mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold text-black"
                        >
                            Want to learn{" "}
                            <span className="text-neutral-400">more?</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={200}>
                        <p
                            data-testid="contact-body"
                            className="mt-6 text-sm md:text-base text-neutral-600 leading-relaxed max-w-md"
                        >
                            For partnerships, campaigns or more information
                            about FreeByte, contact us directly.
                        </p>
                    </Reveal>
                    <Reveal delay={300}>
                        <div className="mt-8 flex flex-wrap items-center gap-5">
                            <a
                                href={MAILTO}
                                data-testid="contact-email-btn"
                                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-black text-white text-[13px] font-medium tracking-wide hover:bg-neutral-800 transition-colors"
                            >
                                Email Us
                                <ArrowUpRight size={15} />
                            </a>
                            <a
                                href={MAILTO}
                                data-testid="contact-email-text"
                                className="text-sm text-neutral-700 link-underline"
                            >
                                {CONTACT_EMAIL}
                            </a>
                        </div>
                    </Reveal>
                </div>

                <div className="lg:col-span-6">
                    <Reveal delay={200}>
                        <form
                            onSubmit={onSubmit}
                            data-testid="contact-form"
                            className="rounded-2xl border border-black/10 bg-white p-7 md:p-8"
                        >
                            <p className="text-[11px] uppercase tracking-[0.22em] text-neutral-400">
                                Send an inquiry
                            </p>
                            <div className="mt-5 space-y-4">
                                <div>
                                    <label
                                        htmlFor="name"
                                        className="text-[11px] tracking-wide text-neutral-500"
                                    >
                                        Name
                                    </label>
                                    <input
                                        id="name"
                                        name="name"
                                        type="text"
                                        required
                                        data-testid="contact-input-name"
                                        className="mt-1.5 w-full h-10 px-0 border-0 border-b border-black/15 bg-transparent text-sm text-black placeholder:text-neutral-400 focus:outline-none focus:border-black transition-colors"
                                        placeholder="Your name"
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="email"
                                        className="text-[11px] tracking-wide text-neutral-500"
                                    >
                                        Email
                                    </label>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        required
                                        data-testid="contact-input-email"
                                        className="mt-1.5 w-full h-10 px-0 border-0 border-b border-black/15 bg-transparent text-sm text-black placeholder:text-neutral-400 focus:outline-none focus:border-black transition-colors"
                                        placeholder="you@email.com"
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="message"
                                        className="text-[11px] tracking-wide text-neutral-500"
                                    >
                                        Message
                                    </label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        rows={4}
                                        required
                                        data-testid="contact-input-message"
                                        className="mt-1.5 w-full px-0 py-2 border-0 border-b border-black/15 bg-transparent text-sm text-black placeholder:text-neutral-400 focus:outline-none focus:border-black transition-colors resize-none"
                                        placeholder="Tell us about your campaign or partnership idea."
                                    />
                                </div>
                            </div>
                            <button
                                type="submit"
                                data-testid="contact-form-submit"
                                className="mt-7 w-full inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-black text-white text-[13px] font-medium tracking-wide hover:bg-neutral-800 transition-colors group"
                            >
                                Send Inquiry
                                <ArrowRight
                                    size={15}
                                    className="transition-transform group-hover:translate-x-0.5"
                                />
                            </button>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

const TrustSection = () => {
    const brands = ["ZUBIZU", "Yandex AI", "Pazarama", "Colgate", "iTunes"];
    return (
        <section
            data-testid="trust-section"
            className="border-t border-black/5 py-24 md:py-32"
        >
            <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
                <Reveal>
                    <p className="text-[11px] uppercase tracking-[0.28em] text-neutral-400">
                        Trusted by Leading Brands
                    </p>
                </Reveal>
                <Reveal delay={100}>
                    <h2
                        data-testid="trust-headline"
                        className="mt-5 text-2xl sm:text-[28px] lg:text-[34px] font-medium tracking-[-0.02em] leading-[1.15] text-neutral-800"
                    >
                        Turkey&rsquo;s{" "}
                        <span className="text-black font-semibold">#1</span>{" "}
                        rewarded growth engine.
                    </h2>
                </Reveal>
                <Reveal delay={200}>
                    <p
                        data-testid="trust-body"
                        className="mt-5 text-sm md:text-[15px] text-neutral-500 leading-relaxed max-w-xl mx-auto"
                    >
                        FreeByte helps leading brands drive engagement through
                        mobile rewards and loyalty experiences.
                    </p>
                </Reveal>

                <Reveal delay={300}>
                    <div
                        data-testid="trust-brand-wall"
                        className="mt-16 md:mt-20 flex flex-wrap items-center justify-center gap-x-10 gap-y-6 md:gap-x-16"
                    >
                        {brands.map((b, i) => (
                            <span
                                key={b}
                                data-testid={`trust-brand-${b.toLowerCase().replace(/\s+/g, "-")}`}
                                className="text-[12px] md:text-[13px] font-medium tracking-[0.18em] uppercase text-neutral-400 opacity-60 hover:opacity-100 hover:text-neutral-800 transition-all duration-500"
                                style={{ transitionDelay: `${i * 40}ms` }}
                            >
                                {b}
                            </span>
                        ))}
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

const Home = () => {
    return (
        <main data-testid="home-page">
            <HeroSection />
            <HowItWorksSection />
            <TrustSection />
            <ContactSection />
        </main>
    );
};

export default Home;
